import { MoreHorizontal, Pencil, Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ShippingRate, ShippingZone } from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@nucleus-commerce/ui/components/dropdown-menu';

import { countryName } from '@/features/markets';

import { regionName } from '../schemas/regions';
import { regionRestrictionCount } from '../schemas/shipping-rules';

import { RatesTable, type RateAction } from './rates-table';

export type ZoneAction = 'edit' | 'delete' | 'addRate';

export interface ZoneCardProps {
  zone: ShippingZone;
  /** Shows the zone and rate actions; false for read-only roles and closed stores. */
  editable: boolean;
  onZoneAction: (action: ZoneAction, zone: ShippingZone) => void;
  onRateAction: (action: RateAction, rate: ShippingRate, zone: ShippingZone) => void;
  /** The one card the shipping tour points into. */
  tour?: boolean;
}

/** One zone: its countries as chips and its rates as a table. */
export function ZoneCard({ zone, editable, onZoneAction, onRateAction, tour }: ZoneCardProps) {
  const { t } = useTranslation('shipping');
  const regions = regionRestrictionCount(zone);

  return (
    <Card
      className="gap-0 py-0"
      data-testid="zone-card"
      data-tour={tour ? 'settings.shipping.zone' : undefined}
    >
      <CardHeader className="flex flex-row items-start justify-between gap-3 border-b py-4">
        <div className="min-w-0 space-y-2">
          <CardTitle className="flex flex-wrap items-center gap-2">
            <span className="truncate">{zone.name}</span>
            <span className="text-muted-foreground text-xs font-normal">
              {t('zone.rateCount', { count: zone.rates.length })}
              {regions > 0 ? ` · ${t('zone.regionCount', { count: regions })}` : null}
            </span>
          </CardTitle>
          <ul className="flex flex-wrap gap-1.5" aria-label={t('zone.countries')}>
            {zone.countries.map((code) => {
              const picked = zone.regions?.[code];
              return (
                <li key={code}>
                  <Badge
                    variant="secondary"
                    title={picked ? picked.map((r) => regionName(code, r)).join(', ') : code}
                  >
                    {countryName(code)}
                    {picked ? (
                      <span className="text-muted-foreground ml-1 font-normal">
                        {t('zone.regions.summary', { count: picked.length })}
                      </span>
                    ) : null}
                  </Badge>
                </li>
              );
            })}
          </ul>
        </div>
        {editable ? (
          <div className="flex shrink-0 items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onZoneAction('addRate', zone)}
              data-tour={tour ? 'settings.shipping.add-rate' : undefined}
            >
              <Plus aria-hidden />
              {t('zone.addRate')}
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('zone.menu', { name: zone.name })}
                >
                  <MoreHorizontal aria-hidden />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onSelect={() => onZoneAction('edit', zone)}>
                  <Pencil aria-hidden />
                  {t('zone.edit')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => onZoneAction('delete', zone)}
                >
                  <Trash2 aria-hidden />
                  {t('zone.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : null}
      </CardHeader>
      <CardContent className="px-0" data-tour={tour ? 'settings.shipping.rates' : undefined}>
        {zone.rates.length === 0 ? (
          <p className="text-muted-foreground px-6 py-4 text-sm">{t('zone.noRates')}</p>
        ) : (
          <RatesTable
            rates={zone.rates}
            editable={editable}
            onAction={(action, rate) => onRateAction(action, rate, zone)}
          />
        )}
      </CardContent>
    </Card>
  );
}
